import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const WINDOWS = [
  { key: 'prime', label: 'Prime 8–12 yrs', icon: 'fa-bullseye', tone: 'bg-emerald-500' },
  { key: 'aging', label: 'Aging 12–20 yrs', icon: 'fa-hammer', tone: 'bg-amber-500' },
  { key: 'early', label: 'New < 8 yrs', icon: 'fa-seedling', tone: 'bg-sky-500' },
  { key: 'overdue', label: 'Overdue 20+ yrs', icon: 'fa-triangle-exclamation', tone: 'bg-rose-500' },
  { key: 'unknown', label: 'No roof permit', icon: 'fa-circle-question', tone: 'bg-slate-400' },
  { key: 'all', label: 'All checked', icon: 'fa-list', tone: 'bg-slate-700' },
];

export default function RoofAgeTrigger() {
  const [window_, setWindow] = useState('prime');
  const [city, setCity] = useState('');
  const [geo, setGeo] = useState('AZ');
  const [minYears, setMinYears] = useState(8);
  const [maxYears, setMaxYears] = useState(12);
  const [data, setData] = useState({ leads: [], window_counts: {}, unchecked: 0 });
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [mining, setMining] = useState(false);
  const [tracing, setTracing] = useState(null);

  const getContact = async (id) => {
    setTracing(id);
    try {
      const res = await api.post(`/api/leads/${id}/waterfall-enrich`);
      const phone = res.data.best_phone || (res.data.phones || [])[0]?.phone;
      const email = res.data.best_email;
      if (phone) toast.success(`Phone found: ${phone}`);
      else if (email) toast.success(`Email found: ${email}`);
      else toast(res.data.reason ? `No contact found — ${res.data.reason}` : 'No contact found for this owner', { icon: 'ℹ️' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Contact lookup failed');
    } finally {
      setTracing(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/triggers/roof-age', { params: { window: window_, city: city || undefined, limit: 100 } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the roof-age call list');
    } finally {
      setLoading(false);
    }
  }, [window_, city]);

  useEffect(() => { load(); }, [load]);

  const sweep = async () => {
    setSweeping(true);
    try {
      const res = await api.post('/api/triggers/roof-age/sweep', { limit: 25, city: city || undefined });
      const w = res.data.windows || {};
      if (res.data.quota_message) toast.error(res.data.quota_message, { duration: 8000 });
      if (res.data.checked > 0) toast.success(`Checked ${res.data.checked} · ${res.data.prime_count} in the prime window · ${res.data.remaining_unchecked} left`);
      if (res.data.checked > 0 && !res.data.prime_count && w.unknown === res.data.checked) {
        toast('No roofing permits on file for that batch — keep sweeping', { icon: 'ℹ️' });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  const mine = async () => {
    if (!geo.trim()) { toast.error('Enter a state code or ZIP to mine'); return; }
    setMining(true);
    try {
      const res = await api.post('/api/triggers/roof-age/mine', {
        geo: geo.trim(), count: 15, min_years: Number(minYears), max_years: Number(maxYears),
      });
      if (res.data.saved_count) {
        toast.success(`${res.data.saved_count} new homes added — roofs permitted ${res.data.permit_from} to ${res.data.permit_to}`);
      } else {
        toast(`Found ${res.data.found} matches, all already in your database`, { icon: 'ℹ️' });
      }
      setWindow('prime');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mining failed');
    } finally {
      setMining(false);
    }
  };

  const counts = data.window_counts || {};

  return (
    <div className="space-y-6" data-testid="roof-age-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Intent Trigger</p>
          <h1 className="text-3xl font-black tracking-tight text-text-primary">Roof Age Trigger</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            A roof permitted 8–12 years ago is the sweet spot: the roof still has life left, the owner is past the
            spend hangover, and panels will outlive what's up there. These are the doors to knock first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="Filter by city"
            className="input-field w-44"
            data-testid="roof-city-filter"
          />
          <button
            onClick={sweep}
            disabled={sweeping}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold shadow hover:shadow-lg transition-all disabled:opacity-60"
            data-testid="roof-sweep-btn"
          >
            {sweeping
              ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Checking permits…</>
              : <><i className="fa-solid fa-tower-broadcast mr-2" />Sweep next 25 leads</>}
          </button>
        </div>
      </div>

      <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="roof-mine-panel">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Mine a market</label>
            <input
              value={geo}
              onChange={e => setGeo(e.target.value)}
              placeholder="State code or ZIP (e.g. AZ, 80212)"
              className="input-field w-56"
              data-testid="roof-geo-input"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Roof age from</label>
            <input type="number" min="0" max="40" value={minYears} onChange={e => setMinYears(e.target.value)} className="input-field w-24" data-testid="roof-min-years" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">to (yrs)</label>
            <input type="number" min="1" max="50" value={maxYears} onChange={e => setMaxYears(e.target.value)} className="input-field w-24" data-testid="roof-max-years" />
          </div>
          <button
            onClick={mine}
            disabled={mining}
            className="px-6 py-2.5 rounded-xl bg-text-primary text-surface text-sm font-bold hover:opacity-90 transition-all disabled:opacity-60"
            data-testid="roof-mine-btn"
          >
            {mining
              ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Mining permits…</>
              : <><i className="fa-solid fa-house-chimney-crack mr-2" />Mine new homes</>}
          </button>
          <p className="text-[11px] text-text-muted max-w-xs">
            Pulls homes with a roofing permit inside your window straight from the nationwide permit network —
            with real owner names, then scores them with live solar + income data.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {WINDOWS.map(w => (
          <button
            key={w.key}
            onClick={() => setWindow(w.key)}
            className={`text-left p-4 rounded-2xl border transition-all ${window_ === w.key ? 'border-primary shadow-md bg-surface' : 'border-border bg-surface-alt hover:border-primary/40'}`}
            data-testid={`roof-window-${w.key}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-6 h-6 rounded-lg ${w.tone} flex items-center justify-center`}>
                <i className={`fa-solid ${w.icon} text-white text-[10px]`} />
              </span>
              <span className="text-2xl font-black text-text-primary">
                {w.key === 'all' ? Object.values(counts).reduce((a, b) => a + b, 0) : (counts[w.key] || 0)}
              </span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-text-muted">{w.label}</p>
          </button>
        ))}
      </div>

      {data.unchecked > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="roof-unchecked-note">
          <i className="fa-solid fa-circle-info" />
          {data.unchecked} lead{data.unchecked === 1 ? '' : 's'} still haven't been checked against the permit network. Sweep again to grow this list.
        </div>
      )}

      <div className="rounded-2xl border border-border bg-surface overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading call list…</div>
        ) : data.leads.length === 0 ? (
          <div className="p-12 text-center" data-testid="roof-empty">
            <i className="fa-solid fa-house-chimney text-3xl text-text-muted mb-3" />
            <p className="text-sm font-semibold text-text-primary">Nothing in this window yet</p>
            <p className="text-xs text-text-muted mt-1">Run a sweep — every lead gets stamped with its roof age from the permit network.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-alt border-b border-border">
              <tr className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
                <th className="text-left px-5 py-3">Owner / Address</th>
                <th className="text-left px-5 py-3">Roof permit</th>
                <th className="text-left px-5 py-3">Age</th>
                <th className="text-left px-5 py-3">Why now</th>
                <th className="text-left px-5 py-3">Contact</th>
                <th className="text-left px-5 py-3">Score</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.leads.map((l, i) => (
                <tr key={l.id} className="border-b border-border last:border-0 hover:bg-surface-alt/60 transition-colors" data-testid={`roof-lead-row-${i}`}>
                  <td className="px-5 py-3.5">
                    <p className="font-semibold text-text-primary">{l.owner_name || l.name}</p>
                    <p className="text-[11px] text-text-muted">{l.address}{l.city ? `, ${l.city}` : ''} {l.state}</p>
                  </td>
                  <td className="px-5 py-3.5 text-text-muted">{l.roof_permit_date || '—'}</td>
                  <td className="px-5 py-3.5">
                    {l.roof_age_years != null ? (
                      <span className={`px-2 py-1 rounded-lg text-[11px] font-bold ${l.roof_window === 'prime' ? 'bg-emerald-100 text-emerald-700' : 'bg-surface-alt text-text-muted'}`}>
                        {l.roof_age_years} yrs
                      </span>
                    ) : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="px-5 py-3.5 text-[11px] text-text-muted max-w-xs">{l.roof_window_copy || '—'}</td>
                  <td className="px-5 py-3.5">
                    {l.phone ? (
                      <a href={`tel:${l.phone}`} className="text-primary font-semibold" data-testid={`roof-call-${i}`}>{l.phone}</a>
                    ) : (
                      <button
                        onClick={() => getContact(l.id, i)}
                        disabled={tracing === l.id}
                        className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-surface-alt border border-border text-text-primary hover:border-primary transition-all disabled:opacity-50"
                        data-testid={`roof-trace-${i}`}
                      >
                        {tracing === l.id
                          ? <><i className="fa-solid fa-spinner fa-spin mr-1" />finding…</>
                          : <><i className="fa-solid fa-user-magnifying-glass mr-1" />Find contact</>}
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-black text-text-primary">{l.score}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Link to={`/leads/${l.id}`} className="text-xs font-bold text-primary hover:underline" data-testid={`roof-open-${i}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
